import React, { useCallback, useEffect, useRef, useState } from "react";
import UseToggle from "../../Hooks/UseToggle";
import { blackboardTheme, kuroirTheme } from "./themes";
import Editor from "@monaco-editor/react";
import MailTemplatePreview from "./MailTemplatePreview";
import { mailServices } from "../../APIServices/GlobalManagement/mailServices";
import Swal from "sweetalert2";

const MailEditor = ({
  previewHTML,
  setPreviewHTML,
  getSingleMailTemplate,
  singleMailTemplate,
}) => {
  const toggles = UseToggle();
  const editorRef = useRef(null);

  const [code, setCode] = useState(""); // saved code
  const [editorValue, setEditorValue] = useState(""); // live editor value
  const [codeCompile, setCodeCompile] = useState(false);
  const [fontSize, setFontSize] = useState(12);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [copied, setCopied] = useState(false);
  const [theme, setTheme] = useState("kuroir");
  const [isFullScreenMode, setIsFullScreenMode] = useState(false);

  const isDirty = editorValue !== code;

  /* ===================== Compile ===================== */
  const compileNow = () => {
    setCodeCompile(true);

    const html = editorRef.current?.getValue() || "";
    setPreviewHTML(html);
    setTogglePreview(true);

    setTimeout(() => {
      setCodeCompile(false);
    }, 500);
  };

  /* ===================== Save ===================== */
  const updatedCodeMailTemplate = async () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to save mail template?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      const userData = {
        mailId: singleMailTemplate?.mailId,
        codeType: true,
        code: editorValue,
      };

      const data = await mailServices.updatedMailTemplate(userData);

      if (data?.statusCode === 200) {
        setCode(editorValue); // reset dirty state
        getSingleMailTemplate();
      }
    });
  };

  /* ===================== Preview Toggle ===================== */
  const setTogglePreview = (value) => {
    if (typeof toggles?.setToggle === "function") {
      try {
        toggles.setToggle(value);
      } catch {
        toggles.setToggle();
      }
    }
  };

  /* ===================== Stats ===================== */
  const [stats, setStats] = useState({
    line: 1,
    column: 1,
    lines: 1,
  });

  /* ===================== Editor Mount ===================== */
  const handleEditorDidMount = useCallback((editor, monaco) => {
    editorRef.current = editor;

    monaco.editor.defineTheme("kuroir", kuroirTheme);
    monaco.editor.defineTheme("blackboard", blackboardTheme);
    monaco.editor.setTheme(theme);

    const updateStats = () => {
      const position = editor.getPosition();
      const model = editor.getModel();

      setStats({
        line: position.lineNumber,
        column: position.column,
        lines: model.getLineCount(),
      });
    };

    const updateUndoRedo = () => {
      const model = editor.getModel();
      if (model) {
        setCanUndo(model.canUndo());
        setCanRedo(model.canRedo());
      }
    };

    updateStats();
    updateUndoRedo();

    editor.onDidChangeCursorPosition(updateStats);
    editor.onDidChangeModelContent(() => {
      updateStats();
      updateUndoRedo();
    });
  }, []);

  /* ===================== Theme Change ===================== */
  useEffect(() => {
    if (window.monaco) {
      window.monaco.editor.setTheme(theme);
    }
  }, [theme]);

  /* ===================== Load API Code ===================== */
  useEffect(() => {
    if (singleMailTemplate?.code) {
      setCode(singleMailTemplate.code);
      setEditorValue(singleMailTemplate.code);
    }
  }, [singleMailTemplate]);

  /* ===================== Editor Actions ===================== */
  const toggleTheme = () => {
    setTheme((prev) => (prev === "kuroir" ? "blackboard" : "kuroir"));
  };

  const handleZoomIn = () => setFontSize((p) => Math.min(p + 2, 32));
  const handleZoomOut = () => setFontSize((p) => Math.max(p - 2, 8));

  const handleUndo = () => editorRef.current?.trigger("keyboard", "undo", null);
  const handleRedo = () => editorRef.current?.trigger("keyboard", "redo", null);

  const handleCopy = async () => {
    const editor = editorRef.current;
    if (!editor) return;

    const model = editor.getModel();
    const selection = editor.getSelection();
    const text =
      selection && !selection.isEmpty()
        ? model.getValueInRange(selection)
        : model.getValue();

    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  /* ===================== Render ===================== */
  return (
    <>
      <div className={isFullScreenMode ? "full-screen-component p-0" : ""}>
        <div
          className="overflow-hidden"
          style={{
            height: isFullScreenMode
              ? "100vh"
              : "calc(100vh - 121px)",
          }}
        >
          {/* ===================== Toolbar ===================== */}
          <div
            className="justify-content-between toolbar border-bottom hstack"
            style={{ height: 36 }}
          >
            <div className="hstack gap-1">
              {/* Save */}
              <div className="p-2 border-end">
                <i
                  title="Save"
                  className="fa-solid fa-floppy-disk CP"
                  onClick={isDirty ? updatedCodeMailTemplate : undefined}
                  style={{
                    opacity: isDirty ? 1 : 0.4,
                    pointerEvents: isDirty ? "auto" : "none",
                  }}
                />
              </div>

              {/* Compile */}
              <div className="p-2 border-end">
                {codeCompile ? (
                  <span className="CodeRunloader mt-2" />
                ) : (
                  <i
                    title="Compile Now"
                    className="fa-solid fa-play CP"
                    onClick={isDirty ? compileNow : undefined}
                    style={{
                      opacity: isDirty ? 1 : 0.4,
                      pointerEvents: isDirty ? "auto" : "none",
                    }}
                  />
                )}
              </div>

              {/* Undo */}
              <div className="p-2 border-end">
                <i
                  title="Undo"
                  className="fa-solid fa-reply CP"
                  onClick={canUndo ? handleUndo : undefined}
                  style={{ opacity: canUndo ? 1 : 0.4 }}
                />
              </div>

              {/* Redo */}
              <div className="p-2 border-end">
                <i
                  title="Redo"
                  className="fa-solid fa-share CP"
                  onClick={canRedo ? handleRedo : undefined}
                  style={{ opacity: canRedo ? 1 : 0.4 }}
                />
              </div>

              {/* Copy */}
              <div className="p-2 border-end">
                <i
                  title="Copy"
                  className={`fa-solid ${
                    copied ? "fa-circle-check" : "fa-copy"
                  } CP`}
                  onClick={handleCopy}
                />
                {copied && <span className="ms-1">Copied</span>}
              </div>

              {/* Zoom */}
              <div className="p-2 border-end">
                <i
                  title="Zoom In"
                  className="fa-solid fa-plus CP"
                  onClick={handleZoomIn}
                />
              </div>
              <div className="p-2 border-end">
                <i
                  title="Zoom Out"
                  className="fa-solid fa-minus CP"
                  onClick={handleZoomOut}
                />
              </div>

              {/* Fullscreen */}
              <div className="p-2 border-end">
                <i
                  title={isFullScreenMode ? "Exit Fullscreen" : "Fullscreen"}
                  className={`fa-solid ${
                    isFullScreenMode ? "fa-minimize" : "fa-maximize"
                  } CP`}
                  onClick={() => setIsFullScreenMode(!isFullScreenMode)}
                />
              </div>

              {/* Theme */}
              <div className="p-2 border-end">
                <i
                  title="Toggle Theme"
                  className={`fa-solid ${
                    theme === "kuroir" ? "fa-sun" : "fa-moon"
                  } CP`}
                  onClick={toggleTheme}
                />
              </div>
            </div>

            {/* Stats */}
            <div className="hstack gap-1 pe-2" style={{ fontSize: 12 }}>
              <span>Line: {stats.line}</span>,
              <span>Col: {stats.column}</span>,
              <span>Lines: {stats.lines}</span>
            </div>
          </div>

          {/* ===================== Editor ===================== */}
          <Editor
            theme={theme}
            defaultLanguage="html"
            value={editorValue}
            onChange={(val) => setEditorValue(val || "")}
            onMount={handleEditorDidMount}
            options={{
              fontSize,
              wordWrap: "on",
              minimap: { enabled: false },
              automaticLayout: true,
            }}
          />
        </div>
      </div>

      {/* ===================== Preview ===================== */}
      <MailTemplatePreview
        Show={!toggles?.toggle}
        Hide={toggles?.setToggle}
        Title="Temp Preview"
        htmlContent={previewHTML}
      />
    </>
  );
};

export default MailEditor;
